import React, { Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import Home from "@pages/Home/Home";
import ILTACON from "@pages/ILTACON/ILTACON";
import { useSelector } from "react-redux";
import { PricingStrategyProvider } from "../components/pages/Dashboard/JuristAi/Jurist/ManageSubscription/PricingContext/PricingStrategyContext";
import ProtectedRoutes from "@pages/Auth/ProtectedRoutes/ProtectedRoutes";
import InternalStaffRoutes from "@pages/Auth/InternalStaffRoutes/InternalStaffRoutes";
import { hasValidStoredSession } from "@lib/utils/authSession";
import { lazyWithRetry } from "@lib/utils/lazyWithRetry";
import { getDashboardLoginRedirectUrl } from "@lib/utils/authRedirect";
import { LEGACY_BLOG_POST_REDIRECTS } from "./legacyBlogRedirects";
import { isSupportedAppId } from "./supportedAppId";
import { COMPARISONS } from "@lib/compare/comparisons";

const LogIn = lazyWithRetry(() => import("@pages/Auth/LogIn/LogIn"), "login");
const InviteLink = lazyWithRetry(() => import("@pages/Auth/InviteLink/InviteLink"), "invite-link");
// Firm-wide connector pages (moved from the criminal app: connections belong to
// the account/firm, never to a practice area).
const FirmEmailIntegrations = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/EmailIntegrations/EmailIntegrations"), "FirmEmailIntegrations");
const FirmFilesConnectionPage = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Connections/FilesConnectionPage"), "FirmFilesConnectionPage");
const FirmOneDriveConnectionPage = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Connections/OneDriveConnectionPage"), "FirmOneDriveConnectionPage");
const FirmSsoPage = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Connections/FirmSsoPage"), "FirmSsoPage");
const FirmDocusign = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/Docusign/Docusign"), "FirmDocusign");
const FirmDocusignCallback = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/DocusignCallback/DocusignCallback"), "FirmDocusignCallback");
const FirmCarta = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/Carta/Carta"), "FirmCarta");
const FirmCartaCallback = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/CartaCallback/CartaCallback"), "FirmCartaCallback");
const FirmCalendarIntegrations = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/CalendarIntegrations/CalendarIntegrations"), "FirmCalendarIntegrations");
const FirmConferencingIntegrations = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/ConferencingIntegrations/ConferencingIntegrations"), "FirmConferencingIntegrations");
const FirmSlackIntegrations = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/SlackIntegrations/SlackIntegrations"), "FirmSlackIntegrations");
const FirmWhatsAppIntegrations = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/WhatsAppIntegrations/WhatsAppIntegrations"), "FirmWhatsAppIntegrations");
const FirmTeamsIntegrations = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/TeamsIntegrations/TeamsIntegrations"), "FirmTeamsIntegrations");
const FirmTeamsOAuthCallback = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/TeamsOAuthCallback/TeamsOAuthCallback"), "FirmTeamsOAuthCallback");
const PublicBookingManager = lazyWithRetry(() => import("@pages/Home/Scheduling/BookingManager"), "PublicBookingManager");
const FirmOtterIntegrations = lazyWithRetry(() => import("@pages/Dashboard/Atticus/Integrations/OtterIntegrations/OtterIntegrations"), "FirmOtterIntegrations");
const Products = lazyWithRetry(() => import("@pages/Products/Products"), "products");
const StartupVcPage = lazyWithRetry(
  () => import("@pages/Products/StartupVcPage"),
  "startup-vc-page"
);
const CriminalPracticeArea = lazyWithRetry(
  () => import("@pages/Criminal/CriminalPracticeArea"),
  "criminal-practice-area"
);
const CivilPracticeArea = lazyWithRetry(
  () => import("@pages/Civil/CivilPracticeArea"),
  "civil-practice-area"
);
const SignUp = lazyWithRetry(() => import("@pages/Auth/SignUp/SignUp"), "signup");
const DashboardLayout = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Layout/Layout"),
  "dashboard-layout"
);
const WelcomeJurist = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/WelcomeJurist/WelcomeJurist"),
  "welcome-jurist"
);
const AccountManagerChatPage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/AccountManagerChatPage/AccountManagerChatPage"),
  "account-manager-chat-page"
);
const CaseflowEntry = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/CaseflowEntry/CaseflowEntry"),
  "caseflow-entry"
);
const LogOut = lazyWithRetry(() => import("@pages/Auth/LogOut/LogOut"), "logout");
const Layout = lazyWithRetry(() => import("@pages/Home/_Layout/Layout"), "home-layout");
const ConfirmAccount = lazyWithRetry(
  () => import("@pages/Auth/ConfirmAccount/ConfirmAccount"),
  "confirm-account"
);
const ManageSubscription = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/ManageSubscription/ManageSubscription"),
  "manage-subscription"
);
const ManageOrganization = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/ManageOrganization/ManageOrganization/ManageOrganization"),
  "manage-organization"
);
const AccountOption = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/AccountOption/AccountOption"),
  "account-option"
);
const FirmWorkspace = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/FirmWorkspace"),
  "firm-workspace"
);
const WorkspaceCasesPage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/WorkspaceCasesPage"),
  "firm-workspace-cases-page"
);
const WorkspaceCasePage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/WorkspaceCasePage"),
  "firm-workspace-case-page"
);
const WorkspaceTaskDetailPage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/WorkspaceTaskDetailPage"),
  "firm-workspace-task-detail-page"
);
const WorkspaceTasksPage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/WorkspaceTasksPage"),
  "firm-workspace-tasks-page"
);
const WorkspaceCalendarPage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/WorkspaceCalendarPage"),
  "firm-workspace-calendar-page"
);
const WorkspaceBillingPage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/WorkspaceBillingPage"),
  "firm-workspace-billing-page"
);
const WorkspaceSignaturesPage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/WorkspaceSignaturesPage"),
  "firm-workspace-signatures-page"
);
const WorkspaceSchedulingPage = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/FirmWorkspace/WorkspaceSchedulingPage"),
  "firm-workspace-scheduling-page"
);

const ShortCanonicalCasePathRedirect = () => {
  const { appId, caseId, caseName, view } = useParams();
  const location = useLocation();
  const allowedApp = isSupportedAppId(appId);
  if (!allowedApp || !caseId) {
    return <Navigate to="/" replace />;
  }

  const segments = ["/dashboard", appId, "cases", caseId, caseName, view || "overview"]
    .filter(Boolean)
    .map((segment, index) => (index === 0 ? segment : encodeURIComponent(segment)));
  return (
    <Navigate
      to={`${segments.join("/")}${location.search || ""}${location.hash || ""}`}
      replace
    />
  );
};

const SupportedAppRoute = () => {
  const { appId } = useParams();
  return isSupportedAppId(appId) ? <Outlet /> : <NotFound />;
};

const appendLocationSuffix = (path, location) =>
  `${path}${location.search || ""}${location.hash || ""}`;

const DashboardLoginRedirect = () => {
  const { appId } = useParams();
  const location = useLocation();
  return <Navigate to={getDashboardLoginRedirectUrl(appId, location)} replace />;
};

// Public scheduling links were briefly emitted with the private app prefix
// (`/dashboard/4/schedule/...`). Keep already-shared links usable while the
// workspace emits the canonical public `/schedule/...` form.
const LegacyPublicSchedulingRedirect = () => {
  const { scopeId, slug } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={appendLocationSuffix(`/schedule/${scopeId}/${slug}`, location)}
      replace
    />
  );
};

const LegacyPublicSchedulingBookingRedirect = ({ mode }) => {
  const { bookingUid } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={appendLocationSuffix(`/schedule/booking/${bookingUid}/${mode}`, location)}
      replace
    />
  );
};

// The canonical public root stays on JuristAI so authenticated users can move
// between the shared selector and either practice-area application without a
// cross-domain hop.
const PublicRoot = () => <Home />;
const InternalOps = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/InternalOps/InternalOps"),
  "internal-ops"
);
const InternalOpsDeveloper = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/InternalOps/InternalOpsDeveloper"),
  "internal-ops-developer"
);
const ChangeProcess = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/ChangeProcess/ChangeProcess"),
  "change-process"
);
const TrialLinks = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/Jurist/TrialLinks/TrialLinks"),
  "trial-links"
);
const TestPage = lazyWithRetry(() => import("@pages/Home/Test/Test"), "test-page");
const TermsOfService = lazyWithRetry(
  () => import("@pages/Auth/TermsOfService/TermsOfService"),
  "terms-of-service"
);
const PrivacyPolicy = lazyWithRetry(
  () => import("@pages/Auth/PrivacyPolicy/PrivacyPolicy"),
  "privacy-policy"
);
const ContactSales = lazyWithRetry(
  () => import("@pages/Home/BookADemo/ContactSales"),
  "contact-sales"
);
const BookADemo = lazyWithRetry(
  () => import("@pages/Home/BookADemo/BookADemo"),
  "book-a-demo"
);
const Blog = lazyWithRetry(() => import("@pages/Blog/Blog.jsx"), "blog");
const BlogPost = lazyWithRetry(() => import("@pages/Blog/BlogPost/BlogPost"), "blog-post");
const LawRouter = lazyWithRetry(() => import("@pages/Law/LawRouter"), "law-router");
const CiteCheckerLayout = lazyWithRetry(
  () => import("@pages/CiteChecker/_Layout/Layout"),
  "cite-checker-layout"
);
const CiteChecker = lazyWithRetry(() => import("@pages/CiteChecker/CiteChecker"), "cite-checker");
const PrivateAttorney = lazyWithRetry(
  () => import("@pages/Home/Pricing/Firms/PrivateAttorney"),
  "private-attorney"
);
const SmallFirm = lazyWithRetry(
  () => import("@pages/Home/Pricing/Firms/SmallFirms"),
  "small-firm"
);
const BigFirm = lazyWithRetry(() => import("@pages/Home/Pricing/Firms/BigFirms"), "big-firm");
const Pricing = lazyWithRetry(() => import("@pages/Home/Pricing/Firms/Pricing"), "pricing");
const ForgotPassword = lazyWithRetry(
  () => import("@pages/Auth/Password/ForgotPassword/ForgotPassword"),
  "forgot-password"
);
const ResetPassword = lazyWithRetry(
  () => import("@pages/Auth/Password/ResetPassword/ResetPassword"),
  "reset-password"
);
const PasswordResetHandler = lazyWithRetry(
  () => import("@pages/Auth/Password/ResetPassword/PasswordResetHandler"),
  "password-reset-handler"
);
const MsftAuthValidated = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/MsftAuthValidated/MsftAuthValidated"),
  "msft-auth-validated"
);
const GoogleAuthValidated = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/GoogleAuthValidated/GoogleAuthValidated"),
  "google-auth-validated"
);
const YahooAuthValidated = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/YahooAuthValidated/YahooAuthValidated"),
  "yahoo-auth-validated"
);
const ProviderAuthCallbackRoute = lazyWithRetry(
  () =>
    import(
      "@pages/Dashboard/JuristAi/CalendarAuthValidated/ProviderAuthCallbackRoute"
    ),
  "provider-auth-callback-route"
);
const ZoomAuthValidated = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/ZoomAuthValidated/ZoomAuthValidated"),
  "zoom-auth-validated"
);
const WhatsAppAuthValidated = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/WhatsAppAuthValidated/WhatsAppAuthValidated"),
  "whatsapp-auth-validated"
);
const WhatsAppSubscriberLink = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/WhatsAppSubscriberLink/WhatsAppSubscriberLink"),
  "whatsapp-subscriber-link"
);
const SlackAuthValidated = lazyWithRetry(
  () => import("@pages/Dashboard/JuristAi/SlackAuthValidated/SlackAuthValidated"),
  "slack-auth-validated"
);
const CompanyConnectorAuthValidated = lazyWithRetry(
  () =>
    import(
      "@pages/Dashboard/JuristAi/CompanyConnectorAuthValidated/CompanyConnectorAuthValidated"
    ),
  "company-connector-auth-validated"
);
const Watch = lazyWithRetry(() => import("@pages/Watch/Watch"), "watch");
const ProductDocumentation = lazyWithRetry(
  () => import("@pages/Home/ProductDocumentation/ProductDocumentation"),
  "product-documentation"
);
const ZoomIntegration = lazyWithRetry(
  () => import("@pages/Home/ZoomIntegration/ZoomIntegration"),
  "zoom-integration"
);
const ZoomTestPlan = lazyWithRetry(
  () => import("@pages/Home/ZoomIntegration/ZoomTestPlan"),
  "zoom-test-plan"
);
const Changelog = lazyWithRetry(
  () => import("@pages/Public/Changelog/Changelog"),
  "changelog"
);
const FaqPage = lazyWithRetry(() => import("@pages/Home/Faq/FaqPage"), "faq-page");
const ComparePage = lazyWithRetry(() => import("@pages/Compare/ComparePage"), "compare-page");
const FaqDocsPage = lazyWithRetry(() => import("@pages/Docs/Faq/FaqDocsPage"), "faq-docs-page");
const Support = lazyWithRetry(() => import("@pages/Support/Support"), "support");
const SlackInstallation = lazyWithRetry(
  () => import("@pages/Public/SlackInstallation/SlackInstallation"),
  "slack-installation"
);
const NotFound = lazyWithRetry(
  () => import("@pages/Public/NotFound/NotFound"),
  "not-found"
);

function AllRouter() {
  const authState = useSelector((state) => state?.auth);
  const location = useLocation();

  const isAuthenticated = hasValidStoredSession(authState);

  const PublicRoute = ({ children }) => {
    if (isAuthenticated) {
      const newRoute = `/dashboard/subscriptions${location.search}`;
      if (location.search.includes("no-subscription")) {
        return <Navigate to={newRoute} replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route index element={<PublicRoot />} />
        <Route path="/iltacon-2026" element={<ILTACON />} />
        <Route path="/" element={<Layout />}>
          <Route path="logout" element={<LogOut />} />
          <Route
            path="register/confirm-account/:token"
            element={<ConfirmAccount />}
          />
          <Route path="faq" element={<FaqPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="forgot-password" element={<ForgotPassword />} />

        <Route
          path="/login"
          element={
            <PublicRoute>
              <LogIn />
            </PublicRoute>
          }
        />

        {/* Organization invite emails link here. Not a PublicRoute: a
            signed-in invitee must still redeem the invite. */}
        <Route path="/invite" element={<InviteLink />} />

        <Route
          path="/dashboard/:appId/login"
          element={<DashboardLoginRedirect />}
        />

        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignUp />
            </PublicRoute>
          }
        />

        <Route
          path="password-reset/confirm-account/:uid/:token"
          element={
            <PasswordResetHandler>
              <ResetPassword />
            </PasswordResetHandler>
          }
        />

        <Route path="/pricing-cards" element={<Pricing />} />
        {/* PricingPlans (rendered by /pro-se-plan, /small-firm and /big-firm)
            links to "/pricing" — the path SeriesAI and Vienna mount. Without
            this alias that link lands on PageNotFound. */}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/pro-se-plan" element={<PrivateAttorney />} />
        <Route path="/small-firm" element={<SmallFirm />} />
        <Route path="/big-firm" element={<BigFirm />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/customer-support" element={<Support />} />
        <Route path="/slack" element={<SlackInstallation />} />
        <Route path="/contact" element={<ContactSales />} />
        <Route path="/partnerships" element={<ContactSales />} />
        <Route path="/book-a-demo" element={<BookADemo />} />

        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:id" element={<BlogPost />} />
        <Route path="/law/*" element={<LawRouter />} />
        <Route
          path="/cite-checker"
          element={
            <CiteCheckerLayout>
              <CiteChecker />
            </CiteCheckerLayout>
          }
        />
        {LEGACY_BLOG_POST_REDIRECTS.map(([legacyPath, slug]) => (
          <Route
            key={legacyPath}
            path={legacyPath}
            element={<Navigate to={`/blog/${slug}`} replace />}
          />
        ))}
        <Route path="/blog-post/*" element={<Navigate to="/blog" replace />} />

        <Route path="/products" element={<Products />} />
        <Route path="/startups-vc" element={<StartupVcPage />} />
        <Route path="/criminal" element={<CriminalPracticeArea />} />
        <Route path="/civil" element={<CivilPracticeArea />} />
        <Route path="/watch" element={<Watch />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/compare" element={<ComparePage />} />
        {/* One route per published comparison (not /compare/:slug), so an
            unknown slug falls through to NotFound instead of rendering an
            indexable page. */}
        {COMPARISONS.map(({ slug }) => (
          <Route key={slug} path={`/compare/${slug}`} element={<ComparePage slug={slug} />} />
        ))}
        <Route path="/product-documentation" element={<ProductDocumentation />} />
        <Route
          path="/product-documentation/faq-structured-data"
          element={<FaqDocsPage />}
        />
        <Route
          path="/product-documentation/zoom-integration"
          element={<ZoomIntegration />}
        />
        <Route
          path="/zoom-integration"
          element={<Navigate to="/product-documentation/zoom-integration" replace />}
        />
        <Route
          path="/product-documentation/zoom-test-plan"
          element={<ZoomTestPlan />}
        />
        <Route
          path="/zoom-test-plan"
          element={<Navigate to="/product-documentation/zoom-test-plan" replace />}
        />
        <Route
          path="/docs"
          element={<Navigate to="/product-documentation" replace />}
        />
        <Route path="/dev_test" element={<TestPage />} />
        <Route
          path="/dashboard+msft-auth-validated"
          element={
            <ProviderAuthCallbackRoute
              provider="microsoft"
              emailElement={<MsftAuthValidated />}
            />
          }
        />
        <Route
          path="/dashboard+google-auth-validated"
          element={
            <ProviderAuthCallbackRoute
              provider="google"
              emailElement={<GoogleAuthValidated />}
            />
          }
        />
        <Route
          path="/dashboard+yahoo-auth-validated"
          element={<YahooAuthValidated />}
        />
        <Route
          path="/dashboard/zoom-auth-validated"
          element={<ZoomAuthValidated />}
        />
        <Route
          path="/dashboard/teams-auth-validated"
          element={<FirmTeamsOAuthCallback />}
        />
        <Route
          path="/dashboard/whatsapp-auth-validated"
          element={<WhatsAppAuthValidated />}
        />
        <Route
          path="/dashboard/slack-auth-validated"
          element={<SlackAuthValidated />}
        />
        {/* SeriesAI per-company connectors. One registered provider app per
            provider redirects here for every front-end origin; the page relays
            code/state back to the app that started the flow. */}
        <Route
          path="/dashboard/stripe-auth-validated"
          element={<CompanyConnectorAuthValidated />}
        />
        <Route
          path="/dashboard/mailchimp-auth-validated"
          element={<CompanyConnectorAuthValidated />}
        />
        <Route
          path="/dashboard/google-analytics-auth-validated"
          element={<CompanyConnectorAuthValidated />}
        />
        <Route
          path="/dashboard/carta-auth-validated"
          element={<CompanyConnectorAuthValidated />}
        />
        <Route
          path="/dashboard/docusign-auth-validated"
          element={<CompanyConnectorAuthValidated />}
        />

        <Route
          path="/dashboard/:appId/schedule/:scopeId/:slug"
          element={<LegacyPublicSchedulingRedirect />}
        />
        <Route
          path="/schedule/booking/:bookingUid/:mode"
          element={<PublicBookingManager />}
        />
        <Route
          path="/dashboard/:appId/schedule/booking/:bookingUid/confirm"
          element={<LegacyPublicSchedulingBookingRedirect mode="confirm" />}
        />
        <Route
          path="/dashboard/:appId/schedule/booking/:bookingUid/cancel"
          element={<LegacyPublicSchedulingBookingRedirect mode="cancel" />}
        />
        <Route
          path="/dashboard/:appId/schedule/booking/:bookingUid/reschedule"
          element={<LegacyPublicSchedulingBookingRedirect mode="reschedule" />}
        />

        <Route element={<ProtectedRoutes />}>
          <Route path="/whatsapp/link" element={<WhatsAppSubscriberLink />} />
          <Route path="/caseflow" element={<CaseflowEntry />} />
          {/* Legacy shared connector entry points are firm-owned. */}
          <Route
            path="/connections"
            element={<Navigate to="/dashboard/connections" replace />}
          />
          <Route
            path="/connections/files"
            element={<Navigate to="/dashboard/connections" replace />}
          />
          <Route
            path="/:appId/cases/:caseId/:caseName/:view"
            element={<ShortCanonicalCasePathRedirect />}
          />
          <Route
            path="/:appId/cases/:caseId/:view"
            element={<ShortCanonicalCasePathRedirect />}
          />
          <Route
            path="/dashboard/:appId"
            element={<SupportedAppRoute />}
          >
            <Route
              element={
                <PricingStrategyProvider>
                  <DashboardLayout />
                </PricingStrategyProvider>
              }
            >
              <Route index element={<WelcomeJurist />} />
              <Route path="integrations/email" element={<FirmEmailIntegrations />} />
              <Route path="connections/email" element={<FirmEmailIntegrations />} />
              {/* Preserve app-scoped connector URLs while moving ownership to
                  the firm-level dashboard. No app id is carried forward. */}
              <Route
                path="connections"
                element={<Navigate to="/dashboard/connections" replace />}
              />
              <Route
                path="connections/files"
                element={<Navigate to="/dashboard/connections" replace />}
              />
              <Route path="cases" element={<WorkspaceCasesPage />} />
              <Route path="cases/:caseId" element={<WorkspaceCasePage view="overview" />} />
              <Route path="cases/:caseId/overview" element={<WorkspaceCasePage view="overview" />} />
              <Route path="cases/:caseId/:caseName/overview" element={<WorkspaceCasePage view="overview" />} />
              <Route path="cases/:caseId/timeline" element={<WorkspaceCasePage view="timeline" />} />
              <Route path="cases/:caseId/:caseName/timeline" element={<WorkspaceCasePage view="timeline" />} />
              <Route path="cases/:caseId/email-timeline" element={<WorkspaceCasePage view="email-timeline" />} />
              <Route path="cases/:caseId/:caseName/email-timeline" element={<WorkspaceCasePage view="email-timeline" />} />
              <Route path="cases/:caseId/tasks" element={<WorkspaceCasePage view="tasks" />} />
              <Route path="cases/:caseId/:caseName/tasks" element={<WorkspaceCasePage view="tasks" />} />
              <Route path="cases/:caseId/tasks/:taskId" element={<WorkspaceTaskDetailPage />} />
              <Route path="cases/:caseId/:caseName/tasks/:taskId" element={<WorkspaceTaskDetailPage />} />
              <Route path="cases/:caseId/calendar" element={<WorkspaceCasePage view="calendar" />} />
              <Route path="cases/:caseId/:caseName/calendar" element={<WorkspaceCasePage view="calendar" />} />
              {/* The left nav app-scopes every destination through
                  buildAppScopedDashboardPath, so from any /dashboard/:appId
                  page it links to /dashboard/:appId/<segment>. Only the case
                  routes above existed here, so ten of the eleven nav items fell
                  through to the catch-all NotFound. These pages are firm-wide;
                  the practice area is carried in the URL for context only, so
                  they render exactly the same components as the firm-level
                  block below. */}
              <Route path="firm" element={<FirmWorkspace mode="home" />} />
              <Route path="tasks" element={<WorkspaceTasksPage />} />
              <Route path="calendar" element={<WorkspaceCalendarPage />} />
              <Route path="billing" element={<WorkspaceBillingPage />} />
              <Route path="signatures" element={<WorkspaceSignaturesPage />} />
              <Route path="scheduling" element={<WorkspaceSchedulingPage />} />
              <Route path="subscriptions" element={<ManageSubscription />} />
              <Route path="organization" element={<ManageOrganization />} />
              <Route path="account" element={<AccountOption />} />
              <Route element={<InternalStaffRoutes />}>
                <Route path="internal-ops" element={<InternalOps />} />
                <Route path="developer" element={<InternalOpsDeveloper />} />
                <Route path="change-process" element={<ChangeProcess />} />
                <Route path="trial-links" element={<TrialLinks />} />
              </Route>
            </Route>
          </Route>
          <Route
            path="/dashboard"
            element={
              <PricingStrategyProvider>
                <DashboardLayout />
              </PricingStrategyProvider>
            }
          >
            <Route index element={<WelcomeJurist />} />
            <Route path="chat" element={<AccountManagerChatPage />} />
            <Route path="firm" element={<FirmWorkspace mode="home" />} />
            <Route path="connections" element={<FirmWorkspace mode="connections" />} />
            {/* Firm-wide connectors belong to the shared JuristAI dashboard,
                not to a practice-area app. Keep the old paths navigable while
                the firm workspace owns the actual connection surface. */}
            {/* Firm-wide connector pages: one surface in this dashboard for every
                practice area. OAuth callback paths are unchanged. */}
            <Route path="connections/email" element={<FirmEmailIntegrations />} />
            <Route path="connections/files" element={<FirmFilesConnectionPage />} />
            <Route path="connections/onedrive" element={<FirmOneDriveConnectionPage />} />
            <Route path="connections/sso" element={<FirmSsoPage />} />
            <Route path="integrations/docusign" element={<FirmDocusign />} />
            <Route path="integrations/docusign/callback" element={<FirmDocusignCallback />} />
            <Route path="integrations/carta" element={<FirmCarta />} />
            <Route path="integrations/carta/callback" element={<FirmCartaCallback />} />
            <Route path="integrations/calendar" element={<FirmCalendarIntegrations />} />
            <Route path="integrations/conferencing" element={<FirmConferencingIntegrations />} />
            <Route path="integrations/slack" element={<FirmSlackIntegrations />} />
            <Route path="integrations/whatsapp" element={<FirmWhatsAppIntegrations />} />
            <Route path="integrations/teams" element={<FirmTeamsIntegrations />} />
            <Route path="integrations/otter" element={<FirmOtterIntegrations />} />
            <Route path="integrations/email" element={<Navigate to="/dashboard/connections/email" replace />} />
            <Route path="cases" element={<WorkspaceCasesPage />} />
            <Route
              path="cases/:appId/:caseId"
              element={<WorkspaceCasePage view="overview" />}
            />
            <Route
              path="cases/:appId/:caseId/overview"
              element={<WorkspaceCasePage view="overview" />}
            />
            <Route
              path="cases/:appId/:caseId/:caseName/overview"
              element={<WorkspaceCasePage view="overview" />}
            />
            <Route
              path="cases/:appId/:caseId/timeline"
              element={<WorkspaceCasePage view="timeline" />}
            />
            <Route
              path="cases/:appId/:caseId/:caseName/timeline"
              element={<WorkspaceCasePage view="timeline" />}
            />
            <Route
              path="cases/:appId/:caseId/tasks"
              element={<WorkspaceCasePage view="tasks" />}
            />
            <Route
              path="cases/:appId/:caseId/:caseName/tasks"
              element={<WorkspaceCasePage view="tasks" />}
            />
            <Route
              path="cases/:appId/:caseId/tasks/:taskId"
              element={<WorkspaceTaskDetailPage />}
            />
            <Route
              path="cases/:appId/:caseId/:caseName/tasks/:taskId"
              element={<WorkspaceTaskDetailPage />}
            />
            <Route
              path="cases/:appId/:caseId/calendar"
              element={<WorkspaceCasePage view="calendar" />}
            />
            <Route
              path="cases/:appId/:caseId/:caseName/calendar"
              element={<WorkspaceCasePage view="calendar" />}
            />
            <Route path="tasks" element={<WorkspaceTasksPage />} />
            <Route path="calendar" element={<WorkspaceCalendarPage />} />
            <Route path="billing" element={<WorkspaceBillingPage />} />
            <Route path="signatures" element={<WorkspaceSignaturesPage />} />
            <Route path="scheduling" element={<WorkspaceSchedulingPage />} />
            <Route path="subscriptions" element={<ManageSubscription />} />
            <Route path="organization" element={<ManageOrganization />} />
            <Route path="account" element={<AccountOption />} />
            <Route element={<InternalStaffRoutes />}>
              <Route path="internal-ops" element={<InternalOps />} />
              <Route path="developer" element={<InternalOpsDeveloper />} />
              <Route path="change-process" element={<ChangeProcess />} />
              <Route path="trial-links" element={<TrialLinks />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default AllRouter;
